import { Flex } from 'antd';
import { DateTime, WeekdayNumbers } from 'luxon';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    BORDER_LIGHT,
    COLOR_ITEM,
    GAP_COLUMN_AGENDA,
    HEIGHT_BAR_ITEM_BIG,
    HEIGHT_BAR_ITEM_SMALL,
    WIDTH_COLUMN_AGENDA_ITEM_TASK,
    WIDTH_COLUMN_AGENDA_TIME,
} from '../const';
import { darkenColor, getContrastingColor } from '../darkenColor';
import { getItemBorderDate, getStartOfWeek } from '../functions';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { ItemType } from '../types';

interface ICalendarItemMonth {
    day: WeekdayNumbers;
    item: ItemType | null;
    currentDate: string;
    isInteractive?: boolean;
    staticSize?: boolean;
    isShowLeftDataPanel?: boolean;
    forceCaption?: boolean;
    length?: number;
}

const CalendarItemMonth: React.FC<ICalendarItemMonth> = ({
    length = 1,
    day,
    item,
    currentDate,
    isInteractive,
    staticSize,
    isShowLeftDataPanel,
    forceCaption,
}) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const currentIdItem = useSelector<RootState, string>((state) => state.calendar.currentIdItem);

    if (!item) {
        return (
            <div
                key={`${day}-nulll`}
                style={{
                    width: '100%',
                    height: isInteractive && !staticSize ? HEIGHT_BAR_ITEM_SMALL : HEIGHT_BAR_ITEM_BIG,
                    borderBottom: BORDER_LIGHT,
                }}
            ></div>
        );
    }

    const startDayWeek = getStartOfWeek(DateTime.fromISO(currentDate));
    const dateStart = startDayWeek.plus({ day: day - 1 });
    const dateEnd = startDayWeek.plus({ day: day - 1 + length - 1 });
    const { itemStartDate, itemEndDate } = getItemBorderDate(item);

    const isStartItem = dateStart.toSeconds() === itemStartDate.toSeconds() || day > 1;
    const isEndItem = dateEnd.toSeconds() === itemEndDate.toSeconds();

    const caption =
        forceCaption || isStartItem || isEndItem || (!isStartItem && !isEndItem && day === 4) || staticSize
            ? `${item.numberWBS || ''} ${item.name} (${item.completionPercentage}%)`
            : '';
    // const textAlign = !isStartItem && !isEndItem ? 'center' : isStartItem ? 'left' : 'right';
    const textAlign = 'center';

    const renderItem = () => {
        return (
            <div
                style={{
                    backgroundColor: item!.color,
                    width: `${100 * length}%`,
                    height:
                        (currentIdItem === '' || currentIdItem !== item.id) && isInteractive && !staticSize
                            ? HEIGHT_BAR_ITEM_SMALL
                            : HEIGHT_BAR_ITEM_BIG,
                    borderTopRightRadius: isEndItem ? 10 : 0,
                    borderBottomRightRadius: isEndItem ? 10 : 0,
                    borderTopLeftRadius: isStartItem ? 10 : 0,
                    borderBottomLeftRadius: isStartItem ? 10 : 0,
                    opacity: currentIdItem === '' || currentIdItem === item.id ? 1 : 0.2,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    borderBottom: BORDER_LIGHT,
                }}
                onMouseOver={() => {
                    // if (isInteractive) {
                    dispatch(setterCalendar({ currentIdItem: item?.id }));
                    // }
                }}
                onMouseLeave={() => {
                    // if (isInteractive) {
                    dispatch(setterCalendar({ currentIdItem: '' }));
                    // }
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(setterGantItems({ isItemEdit: true, currentGantItem: item }));
                }}
            >
                {(!isInteractive || currentIdItem === item.id || staticSize) && (
                    <div
                        style={{
                            padding: '0 10px',
                            fontSize: 12,
                            marginTop: -1,
                            fontWeight: 'bold',
                            textAlign: textAlign,
                            overflow: 'hidden',
                            height: '100%',
                            position: 'relative',
                            color: getContrastingColor(item?.color ? item.color : COLOR_ITEM),
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 1,
                                left: 0,
                                bottom: 0,
                                width: `${item.completionPercentage}%`,
                                backgroundColor: darkenColor(item.color!, 2000),
                                transition: 'width 0.5s ease',
                            }}
                        ></div>
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                color: 'black',
                                zIndex: 100,
                                fontSize: 11,
                            }}
                        >
                            {caption}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return isShowLeftDataPanel ? (
        <Flex justify={'start'} gap={GAP_COLUMN_AGENDA}>
            <div style={{ width: WIDTH_COLUMN_AGENDA_TIME }}>
                Ends {DateTime.fromISO(item.endDate).toFormat('dd LLL')}
            </div>
            <div style={{ width: WIDTH_COLUMN_AGENDA_ITEM_TASK }}>{renderItem()}</div>
        </Flex>
    ) : (
        renderItem()
    );
};

export default CalendarItemMonth;
